import React, { FunctionComponent } from "react"; // Import FunctionComponent for cleaner types
import { GradientText } from "./GradientText";
import { IconBaseProps } from "react-icons"; // Correct import for IconBaseProps from root 'react-icons'

// Import raw icons with aliases
import {
  GoOrganization as GoOrganizationRawImport,
  GoPeople as GoPeopleRawImport,
  GoPackage as GoPackageRawImport,
  GoTools as GoToolsRawImport,
  GoBell as GoBellRawImport,
} from "react-icons/go";
import { MdKeyboardArrowRight as MdKeyboardArrowRightRawImport } from "react-icons/md";

// Directly assert the raw imports to Functional Components.
// This is done using 'as unknown as FunctionComponent<IconBaseProps>' to bypass strict checks.
// This is the strongest assertion and should resolve the TS2322 errors.
const GoOrganization: FunctionComponent<IconBaseProps> = GoOrganizationRawImport as unknown as FunctionComponent<IconBaseProps>;
const GoPeople: FunctionComponent<IconBaseProps> = GoPeopleRawImport as unknown as FunctionComponent<IconBaseProps>;
const GoPackage: FunctionComponent<IconBaseProps> = GoPackageRawImport as unknown as FunctionComponent<IconBaseProps>;
const GoTools: FunctionComponent<IconBaseProps> = GoToolsRawImport as unknown as FunctionComponent<IconBaseProps>;
const GoBell: FunctionComponent<IconBaseProps> = GoBellRawImport as unknown as FunctionComponent<IconBaseProps>;
const MdKeyboardArrowRight: FunctionComponent<IconBaseProps> = MdKeyboardArrowRightRawImport as unknown as FunctionComponent<IconBaseProps>;

// Our custom component type now just references React's FunctionComponent
type IconComponentType = FunctionComponent<IconBaseProps>;

const OcticonMap = {
  organization: GoOrganization,
  people: GoPeople,
  package: GoPackage,
  tools: GoTools,
  bell: GoBell,
} satisfies Record<string, IconComponentType>;

const MaterialIconMap = {
  "keyboard-arrow-right": MdKeyboardArrowRight,
} satisfies Record<string, IconComponentType>;

interface NeumorphicButtonProps {
  label?: string;
  onPress: () => void;
  heightBox?: number;
  widthBox?: string;
  borderRadius?: number;
  fontSize?: number;
  className?: string; // Corrected: This prop existed in original code
  hasIcons?: boolean;
  leftIcon?: keyof typeof OcticonMap;
  leftIconColor?: string;
  rightIcon?: keyof typeof MaterialIconMap;
  color?: string;
  hasAvatar?: boolean;
  avatarImage?: string;
  enableCount?: boolean;
  count?: number;
}

export const NeumorphicButton: React.FC<NeumorphicButtonProps> = ({
  label,
  onPress,
  heightBox,
  widthBox,
  borderRadius,
  fontSize,
  className,
  hasIcons,
  leftIcon,
  leftIconColor,
  rightIcon,
  color,
  hasAvatar,
  avatarImage,
  enableCount,
  count,
}) => {
  const style: React.CSSProperties = {
    "--neu-btn-height": heightBox ? `${heightBox}px` : undefined,
    "--neu-btn-width": widthBox || undefined,
    "--neu-btn-border-radius": borderRadius ? `${borderRadius}px` : undefined,
    "--neu-btn-font-size": fontSize ? `${fontSize}px` : undefined,
  } as React.CSSProperties;

  const RenderLeftIcon = () => {
    if (!leftIcon) return null;
    const IconComponent = OcticonMap[leftIcon];
    if (!IconComponent) {
      console.warn(`Icon "${leftIcon}" not found in OcticonMap.`);
      return null;
    }
    return (
      <div className="neu-btn-icon-left">
        <IconComponent size={24} color={leftIconColor || color} />
      </div>
    );
  };

  const RenderRightIcon = () => {
    if (!rightIcon) return null;
    const IconComponent = MaterialIconMap[rightIcon];
     if (!IconComponent) {
      console.warn(`Icon "${rightIcon}" not found in MaterialIconMap.`);
      return null;
    }
    return (
      <div className="neu-btn-icon-right">
        <IconComponent size={24} color={color} />
      </div>
    );
  };

  const RenderAvatar = () => {
    if (!hasAvatar || !avatarImage) return null;
    return (
      <div className="neu-btn-avatar">
        <img src={avatarImage} alt="Avatar" className="neu-btn-avatar-img" />
      </div>
    );
  };

  const RenderCountBadge = () => {
    if (!enableCount || (count ?? 0) <= 0) return null;
    const displayCount = (count ?? 0) > 9 ? "9+" : count;
    return (
      <div className="neu-btn-badge">
        <span className="neu-btn-badge-text">{displayCount}</span>
      </div>
    );
  };

  return (
    <button onClick={onPress} className={`neumorphic-button ${className || ""}`} style={style}>
      {hasIcons ? (
        <>
          {RenderAvatar()}
          {RenderLeftIcon()}
          <span className="neu-btn-label-icon" style={{ color: color }}>
            {label}
          </span>
          {RenderCountBadge()}
          {RenderRightIcon()}
        </>
      ) : (
        <div className="neu-btn-text-content">
          <GradientText size={fontSize || 16} text={label || ""} />
        </div>
      )}
    </button>
  );
};
